import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { CommonService, LocalStorageService, NotificationService } from '@app/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AccountLimitsData, AppProfileData, CurrentUser, TagData } from '@app/models';
import { OrganizationList } from '@app/models/organization.model';
import { HttpErrorResponse } from '@angular/common/http';
import { StatusCode } from '../data/messages';
import { ErrorService } from '@app/helpers';
import { AnalyticService } from '@app/core/analytic.service';
import { APP_PLAN_TIERS_NAME, FREEMIUM_TIER_DISABLED_URLS, appValues } from '../data/values';
import { IntercomService } from '@app/core/intercom.service';

@Injectable()

export class MainResolver  {
    
    constructor(
        private readonly _commonService: CommonService,
        private readonly _localStorageService: LocalStorageService,
        private readonly _notificationService: NotificationService,
        private readonly _analytics: AnalyticService,
        private readonly _intercomService: IntercomService,
        private readonly _router: Router
    ) {}
        
    /**
     * fetch auth data to choose selected organization from previously saved data.
     * Request the current User data for user admin details
     * set user properties to analytics
     * Handle the Errors other than UnAuthorized status, since its already handled in interceptor
     * Requst the Organizations list for user admin(BH admin or Org admin)
     * Set the app profile data with accesstoken, current user, organizations, current Org and current senior center.
     * set the new auth data in local with updated details
     * Handle the Errors other than UnAuthorized status, since its already handled in interceptor
     * @param route 
     * @param state 
     * @returns Observable<AppProfileData>
     */
    resolve(
        route: ActivatedRouteSnapshot, 
        state: RouterStateSnapshot
    ): Observable<AppProfileData> | Promise<AppProfileData> | AppProfileData {

        const currentLocationUrl = window.location.href;
        const isLoginRoute = currentLocationUrl.includes('/auth');
        const authData = this._localStorageService.getAuthData();

        if (!authData?.accessToken) {
            this._router.navigate(['auth/signin']);
            return of({} as AppProfileData);
        }

        this._commonService.setNavLoading(true);

        return this._commonService.getCurrentUser().pipe(
            switchMap((user: CurrentUser) => this.handleUserResponse(user, authData)),
            switchMap((user: CurrentUser) => this.updateUserDetails(user, authData)),
            switchMap((user: CurrentUser) => {
                if (!isLoginRoute) {
                    // switchMap the organization info request
                    return this._commonService.getOrganizations(this.getUpdatedAuthData(user)?.orgId).pipe(
                        switchMap((orgs: OrganizationList[]) => 
                            this.handleOrganizationResponse(orgs, state)), 
                        catchError(() => of([])) 
                    )
                } else {
                    // At this point, the component will load, and organizations are being fetched in the background
                    this.fetchOrganizations(authData, user, state);
                    return of([]);
                }
            }),
            switchMap(() => this.fetchAllTags()),
            catchError(error => this.handleError(error))
        );
    }

    private getUpdatedAuthData(user: CurrentUser) {
        if (user.profile.organisationId == null) {
            return this._localStorageService.getAuthData();
        }
        return null;
    }

    private updateUserDetails(user: CurrentUser, authData: any): Observable<CurrentUser> {
        const appProfileData = this._commonService.appProfileDataAsValue || {};
        appProfileData.user = user;
        appProfileData.accessToken = authData.accessToken;
        appProfileData.hasNoProgramFiltered = authData.hasNoProgramFitlered;
        this._commonService.setAppProfileData(appProfileData);
        return of(user);
    }

    private handleUserResponse(user: CurrentUser, authData: any): Observable<CurrentUser> {

        if (authData.accessToken && user.user.default_org && authData.orgId === -1) {
            authData = { ...authData, orgId: user.user.default_org, centerId: null, role: user.user.role };
            this._localStorageService.setAuthData(authData);
        }

        this._analytics.setAnalytics(user);
        return of(user);
    }

    private fetchOrganizations(authData: any, user: CurrentUser, state: RouterStateSnapshot): void {
        this._commonService.setLoading(true);
        this._commonService.getOrganizations(this.getUpdatedAuthData(user)?.orgId).pipe(
            catchError(() => of([])), 
            switchMap((orgs: OrganizationList[]) => 
                this.handleOrganizationResponse(orgs, state))
        ).subscribe();
    }

    private fetchAccountLimits(authData: any, state: RouterStateSnapshot) {
        const appProfileData = this._commonService.appProfileDataAsValue || {};

        return this._commonService.getAccountLimits(appProfileData.currentOrganization?.id).pipe(
            switchMap((limits: AccountLimitsData | null) => {
                if (limits?.plan === APP_PLAN_TIERS_NAME.FREE &&
                    FREEMIUM_TIER_DISABLED_URLS.some(url => state.url.includes(url))) {
                    this._router.navigate(['/home']);
                }
                return of(appProfileData);
            })
        );
    }

    private handleOrganizationResponse(orgs: OrganizationList[], state: RouterStateSnapshot) {
        let authData = this._localStorageService.getAuthData();

        if (!authData?.accessToken) {
            this._router.navigate(['auth/signin']);
            return of({} as AppProfileData);
        }

        const selectedOrg = orgs.find(org => org.id == authData?.orgId) || orgs[0];
        const appProfileData = this._commonService.appProfileDataAsValue || {};
        appProfileData.organizations = orgs;
        appProfileData.currentOrganization = selectedOrg;
        appProfileData.currentSeniorCenter = selectedOrg?.seniorCenters?.find(sc => sc.id === authData?.centerId) || null;

        authData = {
            ...authData,
            orgId: selectedOrg.id,
            centerId: appProfileData.currentSeniorCenter?.id || null
        };
        this._localStorageService.setAuthData(authData);
        this._commonService.setAppProfileData(appProfileData);
        this._intercomService.initializeUserSession();

        return this.fetchAccountLimits(authData, state);
    }

    private fetchAllTags(): Observable<AppProfileData> {
        const appProfileData = this._commonService.appProfileDataAsValue || {};
        if (appProfileData.user.document_accepted) {
            return this._commonService.getAllTags().pipe(
                map((tags: TagData[]) => {
                    appProfileData.tagData = tags || [];
                    this._commonService.setNavLoading(false);
                    return appProfileData;
                }),
                catchError(error => {
                    this._commonService.setNavLoading(false);
                    return of(appProfileData);
                })
            );
        } else {
            this._commonService.setNavLoading(false);
        }
        return of(appProfileData);
    }

    private handleError(error: any): Observable<AppProfileData> {
        if (error instanceof HttpErrorResponse && error.status !== StatusCode.Unauthorized) {
            this._notificationService.showError(error);
            this._commonService.setApplicationStatus({ ...new ErrorService(error).parsePageError() });
        }
        this._commonService.setNavLoading(false);
        return of({} as AppProfileData);
    }
}